(() => {
  const ELEMENT_NAME = 'curtis-zepto-bridge';

  if (customElements.get(ELEMENT_NAME)) return;

  class CurtisZeptoBridge extends HTMLElement {
    connectedCallback() {
      this._root = this.closest('.curtis-product-section');
      if (!this._root || this._connected) return;

      this._connected = true;
      this._refreshElements();

      this._onZeptoReady = () => this._handleZeptoReady();
      this._onPopState = () => {
        if (this.isOpen) this.close({ fromHistory: true });
      };
      window.addEventListener('pplrAppInitiated', this._onZeptoReady);
      window.addEventListener('popstate', this._onPopState);

      this._setupPreviewLoading();
      this._watchForZeptoPreview();
      this._observePreviewSize();
    }

    disconnectedCallback() {
      if (!this._connected) return;

      window.removeEventListener('pplrAppInitiated', this._onZeptoReady);
      window.removeEventListener('popstate', this._onPopState);
      this._zeptoObserver?.disconnect();
      this._previewImageObserver?.disconnect();
      this._resizeObserver?.disconnect();
      window.clearTimeout(this._animationTimer);
      window.clearTimeout(this._canvasPollTimer);
      this._overlay?.removeEventListener('animationend', this._animationEndHandler);
      this._previewImage?.removeEventListener('load', this._onPreviewImageLoad);
      this._previewImage?.removeEventListener('error', this._onPreviewImageError);

      if (this.isOpen) this._finishClose({ restoreScroll: false });
      this._connected = false;
    }

    get isOpen() {
      return Boolean(this._overlay?.classList.contains('is-open'));
    }

    open() {
      this._refreshElements();

      const breakpoint = Number.parseInt(this.dataset.mobileBreakpoint || '989', 10);
      const isMobile = window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
      if (!isMobile || this.isOpen || !this._previewStage || !this._configBlock) return false;
      if (!this._overlay || !this._previewZone || !this._controlsZone) return false;

      this._previewLocation = this._rememberLocation(this._previewStage, 'preview');
      this._configLocation = this._rememberLocation(this._configBlock, 'controls');
      this._savedScrollY = window.scrollY;

      this._root.classList.add('curtis-configurator-is-open');
      this._overlay.classList.remove('is-closing');
      this._overlay.classList.add('is-open', 'is-animating');
      this._previewZone.append(this._previewStage);
      this._controlsZone.append(this._configBlock);

      document.body.style.top = `-${this._savedScrollY}px`;
      document.body.classList.add('configurator-open');

      this._finishAnimation('is-animating');
      this._pushHistoryState();

      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
        this._verifyPreviewGeometry();
        this.dispatchEvent(new CustomEvent('curtis:configurator-opened', { bubbles: true }));
      });

      window.curtisHaptics?.trigger('medium');
      return true;
    }

    close(options = {}) {
      if (!this.isOpen || this._overlay.classList.contains('is-closing')) return false;

      const { fromHistory = false } = options;
      if (!fromHistory && history.state?.curtisConfigurator === this._historyId) {
        history.back();
      }

      this._overlay.classList.remove('is-animating');
      this._overlay.classList.add('is-closing');
      this._finishAnimation('is-closing', () => this._finishClose(options));
      window.curtisHaptics?.trigger('light');
      return true;
    }

    _refreshElements() {
      this._overlay = this._root?.querySelector('#CurtisMobileConfigurator');
      this._previewZone = this._root?.querySelector('#OverlayPreviewZone');
      this._controlsZone = this._root?.querySelector('#OverlayControlsZone');
      this._previewStage = this._root?.querySelector('.curtis-zepto-preview');
      this._configBlock = this._root?.querySelector('.curtis-block--buy_buttons_or_configure');
    }

    _rememberLocation(node, name) {
      const marker = document.createComment(`curtis-zepto-${name}`);
      const location = { marker, parent: node.parentNode, nextSibling: node.nextSibling };
      node.before(marker);
      return location;
    }

    _restoreLocation(node, location) {
      if (!node || !location) return;

      if (location.marker.isConnected) {
        location.marker.replaceWith(node);
        return;
      }

      if (!location.parent?.isConnected) return;
      if (location.nextSibling?.parentNode === location.parent) {
        location.parent.insertBefore(node, location.nextSibling);
      } else {
        location.parent.append(node);
      }
    }

    _finishClose(options = {}) {
      const { restoreScroll = true, afterClose = null } = options;

      this._restoreLocation(this._previewStage, this._previewLocation);
      this._restoreLocation(this._configBlock, this._configLocation);
      this._previewLocation = null;
      this._configLocation = null;
      this._historyId = null;

      this._overlay?.classList.remove('is-open', 'is-closing', 'is-animating', 'has-step-progress');
      this._root?.classList.remove('curtis-configurator-is-open');
      document.body.classList.remove('configurator-open');
      document.body.style.top = '';

      if (restoreScroll) window.scrollTo(0, this._savedScrollY || 0);

      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
        this._verifyPreviewGeometry();
        this.dispatchEvent(new CustomEvent('curtis:configurator-closed', { bubbles: true }));
        if (typeof afterClose === 'function') afterClose();
      });
    }

    _pushHistoryState() {
      this._historyId = `${this._root.dataset.sectionId || 'product'}-${Date.now()}`;
      history.pushState({ curtisConfigurator: this._historyId }, '');
    }

    _finishAnimation(className, callback) {
      window.clearTimeout(this._animationTimer);
      if (this._animationEndHandler) {
        this._overlay?.removeEventListener('animationend', this._animationEndHandler);
      }
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        this._overlay?.removeEventListener('animationend', onAnimationEnd);
        this._animationEndHandler = null;
        this._overlay?.classList.remove(className);
        callback?.();
      };
      const onAnimationEnd = (event) => {
        if (event.target === this._overlay) finish();
      };

      this._animationEndHandler = onAnimationEnd;
      this._overlay?.addEventListener('animationend', onAnimationEnd);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._animationTimer = window.setTimeout(finish, reducedMotion ? 20 : 500);
    }

    _watchForZeptoPreview() {
      if (this._previewStage?.querySelector('.pplr_preview_wrapper, .pplr_preview_final')) {
        this._handleZeptoReady();
        return;
      }

      if (!this._previewStage) return;
      this._zeptoObserver = new MutationObserver(() => {
        if (!this._previewStage.querySelector('.pplr_preview_wrapper, .pplr_preview_final')) return;
        this._zeptoObserver.disconnect();
        this._handleZeptoReady();
      });
      this._zeptoObserver.observe(this._previewStage, { childList: true, subtree: true });
    }

    _handleZeptoReady() {
      this._waitForCanvasPaint();
      window.requestAnimationFrame(() => {
        this._root?.classList.add('curtis-zepto-ready');
        this._verifyPreviewGeometry();
        this.dispatchEvent(new CustomEvent('curtis:zepto-ready', { bubbles: true }));
      });
    }

    _setupPreviewLoading() {
      const image = this._previewStage?.querySelector('.curtis-zepto-preview__image');
      if (!image || image === this._previewImage) return;

      this._previewImage?.removeEventListener('load', this._onPreviewImageLoad);
      this._previewImage?.removeEventListener('error', this._onPreviewImageError);
      this._previewImageObserver?.disconnect();

      this._previewImage = image;
      this._onPreviewImageLoad = () => {
        image.dataset.curtisLoadState = 'loaded';
        this._markPreviewReady();
      };
      this._onPreviewImageError = () => {
        image.dataset.curtisLoadState = 'error';
        this._markPreviewLoading();
        this._waitForCanvasPaint();
      };

      image.addEventListener('load', this._onPreviewImageLoad);
      image.addEventListener('error', this._onPreviewImageError);

      this._previewImageObserver = new MutationObserver(() => {
        image.dataset.curtisLoadState = 'loading';
        this._markPreviewLoading();
        window.requestAnimationFrame(() => {
          if (image.complete && image.naturalWidth > 0) this._onPreviewImageLoad();
        });
      });
      this._previewImageObserver.observe(image, {
        attributes: true,
        attributeFilter: ['src', 'srcset'],
      });

      if (image.complete) {
        if (image.naturalWidth > 0) {
          this._onPreviewImageLoad();
        } else {
          this._onPreviewImageError();
        }
      } else {
        image.dataset.curtisLoadState = 'loading';
        this._markPreviewLoading();
      }
    }

    _markPreviewLoading() {
      if (!this._previewStage || this._canvasReady) return;
      this._previewStage.classList.remove('curtis-zepto-preview--loaded');
      this._previewStage.classList.add('curtis-zepto-preview--loading');
      this._previewStage.setAttribute('aria-busy', 'true');
    }

    _markPreviewReady() {
      if (!this._previewStage) return;
      this._previewStage.classList.remove('curtis-zepto-preview--loading');
      this._previewStage.classList.add('curtis-zepto-preview--loaded');
      this._previewStage.setAttribute('aria-busy', 'false');
    }

    _waitForCanvasPaint() {
      if (this._canvasReady || !this._previewStage) return;

      window.clearTimeout(this._canvasPollTimer);
      const startedAt = Date.now();
      const checkCanvas = () => {
        const canvas = this._previewStage.querySelector('#pplr_canvas');
        if (this._isCanvasPainted(canvas)) {
          this._canvasReady = true;
          this._markPreviewReady();
          return;
        }

        if (Date.now() - startedAt < 6000) {
          this._canvasPollTimer = window.setTimeout(checkCanvas, 100);
        }
      };

      checkCanvas();
    }

    _isCanvasPainted(canvas) {
      if (!canvas || canvas.width <= 1 || canvas.height <= 1) return false;

      if (!this._paintProbeCanvas) {
        this._paintProbeCanvas = document.createElement('canvas');
        this._paintProbeCanvas.width = 4;
        this._paintProbeCanvas.height = 4;
        this._paintProbeContext = this._paintProbeCanvas.getContext('2d', { willReadFrequently: true });
      }
      if (!this._paintProbeContext) return false;

      try {
        this._paintProbeContext.clearRect(0, 0, 4, 4);
        this._paintProbeContext.drawImage(canvas, 0, 0, 4, 4);
        const pixels = this._paintProbeContext.getImageData(0, 0, 4, 4).data;
        for (let index = 3; index < pixels.length; index += 4) {
          if (pixels[index] > 0) return true;
        }
        return false;
      } catch (error) {
        return error?.name === 'SecurityError';
      }
    }

    _observePreviewSize() {
      if (!this._previewStage || typeof ResizeObserver === 'undefined') return;
      this._resizeObserver = new ResizeObserver(() => this._verifyPreviewGeometry());
      this._resizeObserver.observe(this._previewStage);
    }

    _verifyPreviewGeometry() {
      const stage = this._previewStage;
      const wrapper = stage?.querySelector(':scope > .pplr_preview_wrapper, :scope > .pplr_preview_final');
      if (!stage || !wrapper || !this._root) return;

      const stageRect = stage.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const withinTolerance = Math.abs(stageRect.width - wrapperRect.width) <= 2
        && Math.abs(stageRect.height - wrapperRect.height) <= 2;

      this._root.dataset.previewGeometry = withinTolerance ? 'ready' : 'mismatch';
    }
  }

  customElements.define(ELEMENT_NAME, CurtisZeptoBridge);
})();
